import { selectBackgroundTasksThatShouldPreventWindowClose } from "@/store/background-tasks/background-tasks-selector";
import { useAppSelector } from "@/store/store-hooks";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import {
  Checkmark2Icon,
  FaroText,
  FaroTooltip,
  grey,
  UpdateIcon,
} from "@faro-lotv/flat-ui";
import { Fade, Stack } from "@mui/material";
import { useEffect, useState, type JSX } from "react";

/** Duration in ms to show the saving completed checkmark for, after all tasks are completed. */
const SHOW_SAVING_COMPLETED_DURATION = 5000;

/** Styles for the indicator container */
const INDICATOR_SX = {
  flexDirection: "row",
  alignItems: "center",
  gap: 0.5,
  color: grey[600],
};

/**
 * @returns An indicator showing while there are background tasks that should prevent the window from closing.
 * This informs the user that there are pending changes being saved to the cloud.
 */
export function SavingIndicator(): JSX.Element {
  const shouldPreventWindowClose =
    useAppSelector(selectBackgroundTasksThatShouldPreventWindowClose).length >
    0;
  const [prevShouldPreventWindowClose, setPrevShouldPreventWindowClose] =
    useState(shouldPreventWindowClose);

  const [showSavingCompleted, setShowSavingCompleted] = useState(false);

  // Detects when all tasks are finished saving.
  // For this it needs to be non-exhaustive, to only trigger on transitions of shouldPreventWindowClose changes.
  useNonExhaustiveEffect(() => {
    setShowSavingCompleted(
      !shouldPreventWindowClose && prevShouldPreventWindowClose,
    );

    setPrevShouldPreventWindowClose(shouldPreventWindowClose);
  }, [shouldPreventWindowClose]);

  // Automatically hide the saving completed checkmark after a timeout
  useEffect(() => {
    if (showSavingCompleted) {
      const timeout = setTimeout(() => {
        setShowSavingCompleted(false);
      }, SHOW_SAVING_COMPLETED_DURATION);

      return () => clearTimeout(timeout);
    }
  }, [showSavingCompleted]);

  return (
    <Fade in={shouldPreventWindowClose || showSavingCompleted} unmountOnExit>
      {shouldPreventWindowClose ? (
        <FaroTooltip title="Saving changes to the cloud. Don't close your browser.">
          <Stack sx={INDICATOR_SX}>
            <UpdateIcon fontSize="small" />
            <FaroText variant="bodyS">Saving...</FaroText>
          </Stack>
        </FaroTooltip>
      ) : (
        <FaroTooltip title="Changes saved to the cloud. It's now safe to close your browser.">
          <Stack sx={INDICATOR_SX}>
            <Checkmark2Icon fontSize="small" />
            <FaroText variant="bodyS">Saved</FaroText>
          </Stack>
        </FaroTooltip>
      )}
    </Fade>
  );
}
